import type { GameInstanceLifecycle as ApiLifecycle } from '@playcastdotio/Messaging';
import type { GameInstanceStore, GameInstanceSessionRecord, GameInstanceLifecycle } from '../GameInstanceStore';

/**
 * The host-side lifecycle is a superset of the API-reported lifecycle.
 * `displayCreating` and `stopping` are transient internal states that do not
 * appear in the report — instances in those states are simply omitted.
 *
 * Android multi-instance host Phase 4 (F4): `failed` and `restarting`
 * instances ARE reported (mapped to `idle` / `launching` with
 * `acceptingMatches: false` and a machine-readable `availabilityBlockReason`)
 * so the API learns the instance failed and why, instead of seeing it
 * silently vanish. A failed instance leaves the report only when its record
 * is released (teardown).
 */
const HOST_TO_API_LIFECYCLE: Partial<Record<GameInstanceLifecycle, ApiLifecycle>> = {
  idle: 'idle',
  launching: 'launching',
  ready: 'ready',
  occupied: 'occupied',
  cooldown: 'cooldown',
  failed: 'idle',
  restarting: 'launching',
};

export type HostStateReportPayload = {
  machineId: string;
  machine: {
    geohash?: string;
    coords?: { lat: number; lng: number };
    isCloudHost: boolean;
    guestAccount: boolean;
    /** Effective instance capacity (hostEffectiveMax, Phase 5). */
    maxPlayers?: number;
    /**
     * Applied CONFIG maxPlayers (pre-clamp) from the host capacity store
     * (Phase 8, review fix S2) — the API clears its desired-capacity marker
     * against this; `maxPlayers` alone never matches under clamping.
     */
    configuredMaxPlayers?: number;
    /** Pre-created VMs on this machine (android multi-instance Phase 2). Omitted while unknown. */
    provisionedInstanceCount?: number;
    /** Playjector-reported instance ceiling (android multi-instance Phase 2). Omitted while unknown. */
    maxGameInstances?: number;
  };
  instances: Array<{
    gameInstanceIndex: number;
    campaignId?: string;
    lifecycle: ApiLifecycle;
    acceptingMatches: boolean;
    availabilityBlockReason?: string | null;
    cooldownEndsAt?: number;
    playerCount: number;
    hostPeerId?: string;
  }>;
};

export type MachineInfo = {
  machineId: string;
  geohash?: string;
  coords?: { lat: number; lng: number };
  isCloudHost: boolean;
  guestAccount: boolean;
  maxPlayers?: number;
  /** Applied CONFIG maxPlayers (pre-clamp), Phase 8 review fix S2. */
  configuredMaxPlayers?: number;
};

/**
 * Playjector-reported machine capabilities (android-multi-instance-host
 * Phase 2). Structural mirror of the service layer's `InstanceCapabilities`
 * (services/playjector/interfaces) — kept structural so the coordinator layer
 * does not import service interfaces.
 */
export type MachineInstanceCapabilities = {
  provisionedInstanceCount: number;
  maxGameInstances: number;
};

/** Resolves the machine's reported capabilities; `undefined` = unknown (fields omitted). */
export type MachineCapabilitiesResolver = () => MachineInstanceCapabilities | undefined;

export type InstancePeerIdResolver = (gameInstanceIndex: number) => string | undefined;

const REPORT_INTERVAL_MS = 30_000;

/**
 * Builds and sends `MATCHMAKING_V2_HOST_STATE_REPORT` from the
 * GameInstanceStore. Emits on instance lifecycle changes and on a 30-second
 * heartbeat interval.
 */
export class GameInstanceReportManager {
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private sendFn: ((payload: HostStateReportPayload) => void) | null = null;
  private lastReportJson = '';
  private unsubscribeStore: (() => void) | null = null;
  private flushScheduled = false;

  constructor(
    private readonly store: GameInstanceStore,
    private readonly getMachineInfo: () => MachineInfo | null,
    private getInstancePeerId: InstancePeerIdResolver = () => undefined,
    private getCapabilities: MachineCapabilitiesResolver = () => undefined,
  ) {}

  /** Late-bind the instance peer resolver (peer manager wires after construction). */
  public setInstancePeerResolver(resolver: InstancePeerIdResolver): void {
    this.getInstancePeerId = resolver;
  }

  /** Late-bind the machine capabilities resolver (capabilities provider wires after construction). */
  public setCapabilitiesResolver(resolver: MachineCapabilitiesResolver): void {
    this.getCapabilities = resolver;
  }

  /** Re-evaluate the report outside store changes (e.g. a peer was assigned). */
  public flush(): void {
    this.emitIfChanged();
  }

  public startReporting(send: (payload: HostStateReportPayload) => void): void {
    this.sendFn = send;
    this.unsubscribeStore = this.store.addListener(() => this.scheduleFlush());
    this.intervalHandle = setInterval(() => this.emit(), REPORT_INTERVAL_MS);
    this.emit();
  }

  /** Whether the report loop is live (heartbeat + change-driven emissions). */
  public isReporting(): boolean {
    return this.intervalHandle !== null;
  }

  public stopReporting(): void {
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    this.sendFn = null;
    this.lastReportJson = '';
  }

  /**
   * Whether any instance is in a state that v1 considers "matchmakeable".
   * Used by the legacy pavilion coordinator to derive v1 `HOST_CONFIG`
   * matchmakeable status during migration.
   */
  public isAnyInstanceMatchmakeable(): boolean {
    return this.store.getSessions().some(
      (s) => s.status === 'ready' && this.isReportable(s),
    );
  }

  public buildReport(): HostStateReportPayload | null {
    const machineInfo = this.getMachineInfo();
    if (!machineInfo) return null;

    const instances = this.store.getSessions()
      .filter((s) => this.isReportable(s))
      .map((s) => this.toInstanceReport(s));

    const capabilities = this.getCapabilities();

    return {
      machineId: machineInfo.machineId,
      machine: {
        geohash: machineInfo.geohash,
        coords: machineInfo.coords,
        isCloudHost: machineInfo.isCloudHost,
        guestAccount: machineInfo.guestAccount,
        maxPlayers: machineInfo.maxPlayers,
        // Phase 8 (review fix S2): the applied CONFIG value rides alongside
        // the clamped effective maxPlayers so the API can clear its
        // desired-capacity marker under clamping.
        configuredMaxPlayers: machineInfo.configuredMaxPlayers,
        // Android multi-instance Phase 2: capability fields are optional in
        // the v2 schema and omitted while unknown. `maxGameInstances` is
        // positive-only on the wire, so a reported 0 ("cannot run any") is
        // omitted here — the schema has no representation for it; the
        // effective-max clamp still honors 0 locally.
        provisionedInstanceCount: capabilities?.provisionedInstanceCount,
        maxGameInstances: capabilities && capabilities.maxGameInstances > 0
          ? capabilities.maxGameInstances
          : undefined,
      },
      instances,
    };
  }

  private emit(): void {
    const report = this.buildReport();
    if (!report) return;
    this.lastReportJson = JSON.stringify(report);
    this.sendFn?.(report);
  }

  /**
   * Coalesce a synchronous burst of store changes (e.g. idle → displayCreating
   * → launching → ready) into a single emission carrying the settled state.
   */
  private scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    void Promise.resolve().then(() => {
      this.flushScheduled = false;
      this.emitIfChanged();
    });
  }

  private emitIfChanged(): void {
    const report = this.buildReport();
    if (!report) return;
    const json = JSON.stringify(report);
    if (json === this.lastReportJson) return;
    this.lastReportJson = json;
    this.sendFn?.(report);
  }

  private isReportable(session: GameInstanceSessionRecord): boolean {
    return HOST_TO_API_LIFECYCLE[session.status] !== undefined;
  }

  private toInstanceReport(session: GameInstanceSessionRecord) {
    const lifecycle = HOST_TO_API_LIFECYCLE[session.status];
    if (!lifecycle) throw new Error(`Unreportable lifecycle: ${session.status}`);
    const acceptingMatches = session.status === 'ready' && !session.activeMatchRequestId;
    return {
      gameInstanceIndex: session.gameInstanceIndex,
      campaignId: session.campaignId,
      lifecycle,
      acceptingMatches,
      availabilityBlockReason: acceptingMatches ? null : this.availabilityBlockReason(session),
      cooldownEndsAt: session.status === 'cooldown' ? session.cooldownEndsAt : undefined,
      playerCount: session.playerCount,
      hostPeerId: this.getInstancePeerId(session.gameInstanceIndex),
    };
  }

  private availabilityBlockReason(session: GameInstanceSessionRecord): string {
    // Phase 4 (F4): failed instances report a machine-readable reason — the
    // explicit override (recovery policy's 'recovery_exhausted') wins, then a
    // pre-ready failure is 'launch_failed', otherwise a runtime crash.
    if (session.status === 'failed') {
      return session.availabilityBlockReason
        ?? (session.launchError ? 'launch_failed' : 'instance_failed');
    }
    if (session.status === 'cooldown') return 'cooldown';
    if (session.activeMatchRequestId) return 'match_assigned';
    return session.status;
  }
}
